import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { IBlockUnblockContract } from '@app/shared/models/banking.model';
import { ICardReasonsParams, IContract, IContractCancelReason } from '@app/shared/models/contract.model';
import { BlockReasonParam } from '@app/shared/models/param.model';
import { IDynamicDialogConfig } from '@app/shared/modules/dynamic-dialog/model';
import { SubscriberComponent } from '@app/shared/modules/subscriber/base/subscriber.component.base';
import { AmplitudeService } from '@app/shared/services/amplitude.service';
import { ContractContext } from '@app/shared/services/context/contract.context.service';
import { CustomerContext } from '@app/shared/services/context/customer.context.service';
import { UserContext } from '@app/shared/services/context/user.context.service';
import { ModalService } from '@app/shared/services/modal.service';
import { CardBasicService } from '@app/shared/services/wallet/card-basic.service';
import { BLOCK_REASON_TYPE, CONTRACT_TYPE, COUNTRY, PRODUCT_TYPE, STATUS } from '@app/shared/types/enums';
import { ACTION_EVENT_PROPERTY, AMPLITUDE_EVENT, ACTION_NAME } from '@app/shared/types/enums/amplitude';
import { Scopes } from '@app/shared/types/enums/scopes';
import { AuthService } from '@auth0/auth0-angular';
import { catchError, concatMap, EMPTY, filter, map, Observable, of, Subject, switchMap, takeUntil, timer } from 'rxjs';

@Component({
  selector: 'app-block-unblock-contract-modal',
  templateUrl: './block-unblock-contract-modal.component.html',
  styleUrls: ['./block-unblock-contract-modal.component.sass'],
})
export class BlockUnblockContractModalComponent extends SubscriberComponent implements OnInit, OnDestroy {
  initialData!: IDynamicDialogConfig;
  contract!: IContract | null;
  rppUserId!: string;
  public isLoading = false;
  public blockContractForm!: FormGroup;
  public reasons$: Observable<IContractCancelReason[] | undefined> = EMPTY;
  initialState!: IDynamicDialogConfig;
  private readonly productType = 'CONTRACT';
  private closeModal$ = new Subject<void>();

  STATUS = STATUS;

  constructor(
    private readonly _modalSvc: ModalService,
    public readonly _contractContextService: ContractContext,
    private readonly _cardBasicSvc: CardBasicService,
    private readonly _amplitudeSvc: AmplitudeService,
    private readonly _customerCtxSvc: CustomerContext,
    private readonly _authSvc: AuthService,
    public readonly _userCtxSvc: UserContext
  ) {
    super();
  }

  ngOnInit() {
    this.logInitEvent();
    this.getModalData();
    this.setupSubscriptions();
    this.createForm();
    this.setupModalCloseSubscription();
    this.contract = this._contractContextService.getCurrentContract();

    this.getBlockUnblockReasons();
  }

  private setupModalCloseSubscription(): void {
    this._subscriptions.sink = this._modalSvc
      .getModalData()
      .pipe(
        takeUntil(this.destroy$),
        filter(data => !!data),
        switchMap(() => this._modalSvc['_dialogRef'].afterClosed())
      )
      .subscribe(() => {
        this.closeModal();
      });
  }

  private logInitEvent(): void {
    this._amplitudeSvc.logEvent<ACTION_EVENT_PROPERTY>(AMPLITUDE_EVENT.ACTION_INIT, {
      actionName: ACTION_NAME.BLOCK_CONTRACT,
    });
  }

  private setupSubscriptions(): void {
    this._modalSvc.isPrimaryActionClicked$
      .pipe(
        takeUntil(this.closeModal$),
        filter(value => value),
        switchMap(() => this._authSvc.user$),
        switchMap(agentData => this.createBlockActionParams(agentData)),
        switchMap(params => {
          if (this._modalSvc.isModalOpen()) {
            return this._cardBasicSvc.blockAction(params);
          } else {
            // Si el modal ya está cerrado, devolvemos un Observable que se resuelve inmediatamente
            return of(null);
          }
        }),
        catchError(error => this.handleErrorAndThrow(error))
      )
      .subscribe(() => this.handleResponse());
  }

  private createBlockActionParams(agentData: any): Observable<IBlockUnblockContract> {
    const email_support = agentData?.email || '';

    return this._customerCtxSvc.contextChanged$.pipe(
      takeUntil(this.closeModal$),
      map(user => {
        this.rppUserId = user?.rppUserId || '';
        const params: IBlockUnblockContract = {
          contract_code: this.contract?.contract_code || '',
          reason: this.blockContractForm.get('reason')?.value || '',
          rpp_user_id: user?.rppUserId || '',
          email_support,
          type: 'block',
        };

        return params;
      })
    );
  }

  closeModal() {
    this.closeModal$.next();
    this.closeModal$.complete();
  }

  private handleErrorAndThrow(error: any): Observable<never> {
    this._modalSvc.closeWithErrorMsg();

    throw error;
  }

  private handleResponse(): void {
    this._modalSvc.closeWithPendingMsg();
    this._subscriptions.sink = timer(2000) // wait for 2 seconds to make sure the contract is blocked/unblocked
      .pipe(
        takeUntil(this.closeModal$),
        concatMap(() => this._cardBasicSvc.getFilteredContracts(this.rppUserId)),
        concatMap(contracts => {
          const selectedContract = contracts.find(contract => contract.contract_code === this.contract?.contract_code);
          this._contractContextService.contractChange(selectedContract);

          return of(contracts);
        })
      )
      .subscribe(() => {
        this.closeModal();
      });
  }

  getBlockUnblockReasons() {
    if (this._customerCtxSvc.context?.country === COUNTRY.CO) {
      const blockParams: BlockReasonParam = {
        scope: this.scopeAssign(),
        contractType: this.contract?.type as CONTRACT_TYPE,
        productType: this.productType as PRODUCT_TYPE,
        reasonType: BLOCK_REASON_TYPE.BLOCK,
      };
      this.reasons$ = this._cardBasicSvc.getBlockUnblockReasonsScope(blockParams);
    } else {
      const params: ICardReasonsParams = {
        contract_type: this.contract?.type as CONTRACT_TYPE,
        product_type: this.productType,
      };
      this.reasons$ = this._cardBasicSvc.getBlockUnblockReasons(params);
    }
  }

  createForm() {
    this.blockContractForm = new FormGroup({
      reason: new FormControl('', Validators.required),
    });
    this.onChangesForm();
  }

  getModalData() {
    this._subscriptions.sink = this._modalSvc
      .getModalData()
      .pipe(takeUntil(this.closeModal$))
      .subscribe({
        next: (data: IDynamicDialogConfig) => (this.initialState = data),
      });
  }

  onChangesForm() {
    this._subscriptions.sink = this.blockContractForm.valueChanges.pipe(takeUntil(this.closeModal$)).subscribe(value => {
      if (this.blockContractForm.valid) {
        const data = {
          ...this.initialState,
          modalComponentData: {
            ...this.initialState.modalComponentData,
            acceptButtonTitleDisabled: false,
            formData: value,
          },
        };

        this._modalSvc.setModalData(data);
      }
    });
  }

  scopeAssign() {
    if (this._userCtxSvc.userHasOperation(Scopes.A_CONTRACT_BLOCK)) {
      return Scopes.A_CONTRACT_BLOCK;
    }
    if (this._userCtxSvc.userHasOperation(Scopes.A_CONTRACT_BLOCK_PB)) {
      return Scopes.A_CONTRACT_BLOCK_PB;
    }
    if (this._userCtxSvc.userHasOperation(Scopes.A_CONTRACT_BLOCK_FRAUD)) {
      return Scopes.A_CONTRACT_BLOCK_FRAUD;
    }

    return '';
  }

  ngOnDestroy() {
    this.closeModal();
    this.unsubscribe();
  }
}
